/**
 * ZBA Institutionen Booking — /institutionen
 * For institutional partners booking consultation appointments.
 * Same core flow as /tg and /fb but simpler: no language, no qualification.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProgressIndicator from 'components/ui/ProgressIndicator';
import StepNavigation from 'components/ui/StepNavigation';
// Custom consultation type for institutions (no SocialStepConsultationType)
import StepCalendar from '../multi-step-booking-form/components/StepCalendar';
import StepTimeSlot from '../multi-step-booking-form/components/StepTimeSlot';
import StepSummary from '../multi-step-booking-form/components/StepSummary';
import StepSuccess from '../multi-step-booking-form/components/StepSuccess';
import BookingFooter from '../multi-step-booking-form/components/BookingFooter';
import { checkSlotStillAvailable, BERATER } from 'utils/graphApi';
import { insertBookingLog } from '../../services/apiService';

const TOTAL_STEPS = 6;
function badge(n) { return `Schritt ${n} von ${TOTAL_STEPS}`; }
function localDateStr(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// Steps: 1=type, 2=calendar, 3=timeslot, 4=contact, 5=summary, 6=success

export default function InstitutionenBooking() {
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [consultationType, setConsultationType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedBeraterEmail, setSelectedBeraterEmail] = useState(null);
  const [formData, setFormData] = useState({});
  const [bookingResult, setBookingResult] = useState(null);
  const [slotConflict, setSlotConflict] = useState(false);
  const [duplicateBooking, setDuplicateBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // UTM
  const [utmParams, setUtmParams] = useState({
    utm_source: 'institutionen', utm_medium: 'partner', utm_campaign: '', utm_content: '', utm_term: '',
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setUtmParams({
      utm_source: params.get('utm_source') || 'institutionen',
      utm_medium: params.get('utm_medium') || 'partner',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
    });
  }, [location.search]);

  const goTo = useCallback((s) => { setStep(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsLoading(true);
    setSlotConflict(false);

    // Double-check slot
    try {
      const stillAvailable = await checkSlotStillAvailable(
        localDateStr(selectedDate), selectedSlot,
        consultationType === 'onsite' ? 60 : 30, selectedBeraterEmail
      );
      if (!stillAvailable) { setSlotConflict(true); setIsLoading(false); return; }
    } catch (_) {}

    const datePart = localDateStr(selectedDate).replace(/-/g, '');
    const randPart = Math.random().toString(36).toUpperCase().slice(2, 6);
    const bookingRef = `ZBA-${datePart}-${randPart}`;
    const beraterObj = BERATER?.find(b => b?.email === selectedBeraterEmail);

    // Build institution note for calendar + PD
    const instNote = [
      '📋 Termin über: Institutionen / Partner',
      formData.organisation && `🏛 Organisation: ${formData.organisation}`,
      `👤 ${formData.vorname} ${formData.nachname}`,
      `📧 ${formData.email}`,
      `📞 ${formData.telefonPrefix || '+49'} ${formData.telefon}`,
    ].filter(Boolean).join('\n');

    try {
      const body = {
        vorname: formData.vorname,
        nachname: formData.nachname,
        email: formData.email,
        telefon: formData.telefon,
        wohnort: formData.organisation || '',
        type: consultationType,
        date: localDateStr(selectedDate),
        time: selectedSlot,
        beraterEmail: selectedBeraterEmail,
        beraterName: beraterObj?.name || '',
        booking_ref: bookingRef,
        source: 'institutionen',
        channel: 'institutionen',
        language: 'de',
        event_note: instNote,
        sub_id: formData.organisation || null,
        ...utmParams,
      };

      const res = await fetch('/api/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.duplicate) {
        setDuplicateBooking(data);
      } else if (data.success) {
        setBookingResult({ ...data, bookingRef, beraterName: beraterObj?.name || '' });
        goTo(6);
      } else {
        console.error('[institutionen] Failed:', data.error || 'unknown');
      }
    } catch (err) {
      console.error('[institutionen] Submit error:', err.message);
    }
    setIsLoading(false);
  };

  return (
    <div className="booking-page" style={{ background: '#F0F6FB' }}>
      <div className="booking-main" style={{ maxWidth: 580, margin: '0 auto', padding: '0 16px 40px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', padding: '28px 0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <img src="https://zba.academy/webinar/images/zbalogo2.png" alt="ZBA" style={{ height: 28 }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#3E4954', marginBottom: 6, fontFamily: 'system-ui' }}>
            Termin für Institutionen & Partner
          </h1>
          <p style={{ fontSize: 14, color: '#6B8299', fontFamily: 'system-ui' }}>
            Vereinbaren Sie einen Beratungstermin mit unserem Team
          </p>
        </div>

        <ProgressIndicator current={step} total={TOTAL_STEPS} />

        {/* Step 1: Consultation type */}
        {step === 1 && (
          <div className="booking-card" style={{ padding: '28px 24px', background: '#fff', borderRadius: 16,
            border: '1px solid #D0DFE8', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B8299', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginBottom: 6 }}>{badge(1)}</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#3E4954', marginBottom: 6, fontFamily: 'system-ui' }}>
              Gesprächsart wählen
            </h2>
            <p style={{ fontSize: 14, color: '#6B8299', marginBottom: 20, fontFamily: 'system-ui' }}>
              Wie möchten Sie den Termin wahrnehmen?
            </p>
            {[
              { id: 'onsite', icon: '🏢', label: 'Vor-Ort-Gespräch', desc: 'Persönlich bei uns in Wülfrath · Kruppstraße 41-45, 42489 Wülfrath', rec: true },
              { id: 'phone',  icon: '📞', label: 'Telefonat',        desc: 'Telefonisches Gespräch zur Abstimmung und Zusammenarbeit' },
            ].map(o => (
              <button key={o.id} onClick={() => { setConsultationType(o.id); goTo(2); }} type="button"
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px',
                  marginBottom: 10, borderRadius: 14, cursor: 'pointer', textAlign: 'left',
                  border: `2px solid ${consultationType === o.id ? '#3E4954' : '#D0DFE8'}`,
                  background: consultationType === o.id ? '#D0E5F5' : '#fff',
                  fontFamily: 'system-ui', transition: 'all 140ms' }}>
                <span style={{ fontSize: 28, flexShrink: 0 }}>{o.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#3E4954', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {o.label}
                    {o.rec && <span style={{ fontSize: 10, fontWeight: 700, background: '#166534', color: 'white',
                      padding: '2px 8px', borderRadius: 100, textTransform: 'uppercase' }}>Empfohlen</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B8299', marginTop: 3, lineHeight: 1.4 }}>{o.desc}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Calendar */}
        {step === 2 && (
          <>
            <StepCalendar
              selectedDate={selectedDate}
              onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); setSelectedBeraterEmail(null); goTo(3); }}
              consultationType={consultationType}
              source="institutionen"
              lang="inst"
              stepBadge={badge(2)}
            />
            <StepNavigation onBack={() => goTo(1)} />
          </>
        )}

        {/* Step 3: Time slot */}
        {step === 3 && selectedDate && (
          <>
            <StepTimeSlot
              selectedDate={selectedDate}
              consultationType={consultationType}
              selectedSlot={selectedSlot}
              onSelect={(slot, berater) => { setSelectedSlot(slot); setSelectedBeraterEmail(berater); goTo(4); }}
              onBack={() => goTo(2)}
              source="institutionen"
              lang="inst"
              stepBadge={badge(3)}
            />
            <StepNavigation onBack={() => goTo(2)} />
          </>
        )}

        {/* Step 4: Contact form (with Organisation) */}
        {step === 4 && (
          <>
            <InstitutionContactForm
              onSubmit={(data) => { setFormData(data); goTo(5); }}
              initialData={formData}
              badge={badge(4)}
            />
            <StepNavigation onBack={() => goTo(3)} />
          </>
        )}

        {/* Step 5: Summary */}
        {step === 5 && (
          <>
            <StepSummary
              formData={{
                ...formData,
                telefon: `${formData.telefonPrefix || '+49'} ${formData.telefon}`.trim(),
              }}
              consultationType={consultationType}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              beraterName={BERATER?.find(b => b?.email === selectedBeraterEmail)?.name || ''}
              lang="inst"
              stepBadge={badge(5)}
              isLoading={isLoading}
              onBook={handleSubmit}
              slotConflict={slotConflict}
              duplicateBooking={duplicateBooking}
              onDismissDuplicate={() => setDuplicateBooking(null)}
            />
            <StepNavigation onBack={() => goTo(4)} />
          </>
        )}

        {/* Step 6: Success */}
        {step === 6 && bookingResult && (
          <StepSuccess
            bookingRef={bookingResult.bookingRef}
            beraterName={bookingResult.beraterName}
            consultationType={consultationType}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            formData={formData}
            lang="inst"
            stepBadge={badge(6)}
          />
        )}

        <BookingFooter />
      </div>
    </div>
  );
}

// ── Custom contact form for institutions ─────────────────────────────────────
function InstitutionContactForm({ onSubmit, initialData, badge }) {
  const [form, setForm] = useState({
    vorname: initialData?.vorname || '',
    nachname: initialData?.nachname || '',
    telefon: initialData?.telefon || '',
    telefonPrefix: initialData?.telefonPrefix || '+49',
    email: initialData?.email || '',
    organisation: initialData?.organisation || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.vorname.trim()) e.vorname = true;
    if (!form.nachname.trim()) e.nachname = true;
    if (!form.telefon.trim()) e.telefon = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit(form);
  };

  const inputStyle = (err) => ({
    width: '100%', padding: '13px 16px', borderRadius: 10, fontSize: 15,
    border: `1.5px solid ${err ? '#DC2626' : '#C8DCF0'}`,
    fontFamily: 'system-ui', outline: 'none', background: '#fff',
    transition: 'border-color 150ms',
  });

  const labelStyle = {
    fontSize: 12, fontWeight: 700, color: '#3E4954', textTransform: 'uppercase',
    letterSpacing: '0.07em', marginBottom: 7, display: 'block',
  };

  return (
    <div className="booking-card" style={{ padding: '28px 24px', background: '#fff', borderRadius: 16,
      border: '1px solid #D0DFE8', marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B8299', textTransform: 'uppercase',
        letterSpacing: '0.08em', marginBottom: 6 }}>{badge}</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: '#3E4954', marginBottom: 20, fontFamily: 'system-ui' }}>
        Kontaktdaten
      </h2>

      {/* Organisation — prominent at top */}
      <div style={{ marginBottom: 18, padding: 16, background: '#F0F6FB', borderRadius: 12,
        border: '1.5px solid #D0E5F5' }}>
        <label style={labelStyle}>🏛 Organisation / Institution <span style={{ color: '#6B8299', fontWeight: 400 }}>(optional)</span></label>
        <input style={inputStyle(false)} placeholder="z.B. Agentur für Arbeit, Jobcenter..."
          autoComplete="off" data-lpignore="true" data-1p-ignore="true"
          value={form.organisation} onChange={e => set('organisation', e.target.value)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Vorname *</label>
          <input style={inputStyle(errors.vorname)} autoComplete="off" data-lpignore="true" data-1p-ignore="true"
            value={form.vorname} onChange={e => set('vorname', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Nachname *</label>
          <input style={inputStyle(errors.nachname)} autoComplete="off" data-lpignore="true" data-1p-ignore="true"
            value={form.nachname} onChange={e => set('nachname', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Telefon *</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ ...inputStyle(false), width: 90, flexShrink: 0 }}
            value={form.telefonPrefix} onChange={e => set('telefonPrefix', e.target.value)}>
            <option value="+49">+49</option>
            <option value="+43">+43</option>
            <option value="+41">+41</option>
          </select>
          <input style={inputStyle(errors.telefon)} type="tel" inputMode="tel"
            autoComplete="off" data-lpignore="true" data-1p-ignore="true"
            placeholder="152 12345678"
            value={form.telefon} onChange={e => set('telefon', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>E-Mail *</label>
        <input style={inputStyle(errors.email)} type="text" inputMode="email"
          autoComplete="off" data-lpignore="true" data-1p-ignore="true"
          value={form.email} onChange={e => set('email', e.target.value)} />
      </div>

      {Object.keys(errors).length > 0 && (
        <div style={{ padding: '10px 14px', background: '#FEF2F2', borderRadius: 8,
          color: '#991B1B', fontSize: 13, marginBottom: 16 }}>
          Bitte alle Pflichtfelder (*) ausfüllen.
        </div>
      )}

      <button onClick={handleSubmit} style={{
        width: '100%', height: 52, borderRadius: 12, border: 'none',
        background: '#3E4954', color: 'white', fontSize: 16, fontWeight: 700,
        fontFamily: 'system-ui', cursor: 'pointer',
      }}>
        Weiter →
      </button>
    </div>
  );
}
